import { useEffect } from "react";
import type { ClipWindow } from "../clipTrack";

const BOUNDARY_TOLERANCE_SECONDS = 0.05;
const BOUNDARY_POLL_MS = 100;

/**
 * Makes the active clip range behave like a preview loop.
 *
 * Every fresh play starts at IN, then playback wraps from OUT back to IN.
 * Pausing is left entirely to the user: the boundary check never calls
 * `play()` unless the underlying media itself reached its physical end.
 */
export function useClipLoopPlayback({
  video,
  window: clipWindow,
  enabled,
}: {
  video: HTMLVideoElement | null;
  window: ClipWindow | null;
  enabled: boolean;
}): void {
  useEffect(() => {
    if (!enabled || !video || !clipWindow) return;

    const start = Math.max(0, clipWindow.start);
    const end = Math.max(start + BOUNDARY_TOLERANCE_SECONDS, clipWindow.end);

    const restartAtIn = () => {
      video.currentTime = start;
    };
    const loopAtOut = () => {
      if (
        !video.paused &&
        video.currentTime >= end - BOUNDARY_TOLERANCE_SECONDS
      ) {
        restartAtIn();
      }
    };
    const restartAfterMediaEnd = () => {
      restartAtIn();
      void video.play().catch(() => undefined);
    };

    video.addEventListener("play", restartAtIn);
    video.addEventListener("timeupdate", loopAtOut);
    video.addEventListener("ended", restartAfterMediaEnd);
    const intervalId = window.setInterval(loopAtOut, BOUNDARY_POLL_MS);

    return () => {
      video.removeEventListener("play", restartAtIn);
      video.removeEventListener("timeupdate", loopAtOut);
      video.removeEventListener("ended", restartAfterMediaEnd);
      window.clearInterval(intervalId);
    };
  }, [enabled, video, clipWindow]);
}
